import React from 'react';
import { useBranding } from '../contexts/BrandingContext';
import useVoucherTemplateSettings from '../hooks/useVoucherTemplateSettings';
import ModernTemplate from './templates/ModernTemplate';
import DenseGridTemplate from './templates/DenseGridTemplate';
import ClassicTemplate from './templates/ClassicTemplate';
import CompactTemplate from './templates/CompactTemplate';
import ElegantTemplate from './templates/ElegantTemplate';
import ClassicOrangeTemplate from './templates/ClassicOrangeTemplate';
import ClassicBlueTemplate from './templates/ClassicBlueTemplate';
import SimpleTextTemplate from './templates/SimpleTextTemplate';
import QRVoucherTemplate from './templates/QRVoucherTemplate';
import BatchHeader from './templates/BatchHeader';

// Format Date -> "DD-MM-YYYY HH:mm"
const formatCreatedAt = (raw) => {
  if (!raw) return '';
  try {
    const s = typeof raw === 'string' ? raw.replace(' ', 'T') : raw;
    const d = new Date(s);
    if (isNaN(d.getTime())) return String(raw);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return String(raw);
  }
};

const VoucherPrintTemplate = ({ vouchers, template = 'modern', hotspotGatewayIp, templateSettingsOverride }) => {
  const { branding } = useBranding();
  const { settings: vtSettingsFromHook } = useVoucherTemplateSettings();
  // Editor page passes live-preview override (flat, single template).
  // Production print uses nested per-template settings from DB.
  // templateSettingsOverride: flat object (for editor live-preview)
  // vtSettingsFromHook: nested { simple:{...}, classic:{...}, ... }
  const _tkey = String(template || 'modern').toLowerCase();
  const templateSettings = templateSettingsOverride || (vtSettingsFromHook && vtSettingsFromHook[_tkey]) || null;
  // Voucher dicetak di kertas putih → prioritaskan logo versi hitam/gelap (Portal Pelanggan)
  // Fallback urutan: logo_portal_url → logo_invoice_url → logo_url → logo_base64
  const raw = branding?.logo_portal_url || branding?.logo_invoice_url || branding?.logo_url || branding?.logo_base64 || '';
  const companyLogo = raw && raw.startsWith('data:') ? raw : (raw && !raw.startsWith('http') && !raw.startsWith('/') ? `data:image/png;base64,${raw}` : raw);
  const companyName = branding?.company_name || branding?.app_name || '';

  // Batch header info (per-batch): ambil dari voucher pertama.
  // Voucher yang di-generate bersamaan akan memiliki created_at yang identik,
  // jadi cukup pakai voucher pertama sebagai representasi batch.
  const sample = (vouchers && vouchers[0]) || {};
  const pkgName = sample.package_name || sample.plan_name || sample.service_plan || sample.profile || '';
  const priceStr = sample.price ? `Rp. ${Number(sample.price).toLocaleString('id-ID')}` : '';
  const createdAtStr = formatCreatedAt(sample.created_at) || formatCreatedAt(new Date().toISOString());
  const batchHeader = (
    <BatchHeader
      pkgName={pkgName}
      priceStr={priceStr}
      createdAt={createdAtStr}
      count={vouchers ? vouchers.length : 0}
    />
  );

  // Render template sesuai pilihan
  const commonProps = { vouchers, companyLogo, companyName, hotspotGatewayIp, batchHeader };
  const cfgProps = { ...commonProps, templateSettings };
  switch (template) {
    case 'modern':
      return <ModernTemplate {...commonProps} />;
    case 'classic':
      return <ClassicTemplate {...cfgProps} />;
    case 'classic-orange':
      return <ClassicOrangeTemplate {...cfgProps} />;
    case 'simple':
      return <SimpleTextTemplate {...cfgProps} />;
    case 'classic-blue':
      return <ClassicBlueTemplate {...cfgProps} />;
    case 'compact':
      return <CompactTemplate {...cfgProps} />;
    case 'qr':
      return <QRVoucherTemplate {...cfgProps} />;
    case 'elegant':
      return <ElegantTemplate {...commonProps} />;
    case 'dense50':
      return <DenseGridTemplate {...commonProps} density={50} />;
    case 'dense70':
      return <DenseGridTemplate {...commonProps} density={70} />;
    case 'dense100':
      return <DenseGridTemplate {...commonProps} density={100} />;
    default:
      return <ModernTemplate {...commonProps} />;
  }
};

// Fallback jika ada yang salah (tidak akan digunakan karena sudah pindah ke file terpisah)
const FallbackTemplate = ({ vouchers, companyLogo }) => {
  return (
    <div className="print-container">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          .print-container {
            width: 297mm;
            height: 210mm;
            padding: 3mm;
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            grid-template-rows: repeat(5, 1fr);
            gap: 2mm;
            page-break-after: always;
          }
          
          .voucher-card {
            width: 100%;
            height: 100%;
            border: 2px solid #e5e7eb;
            border-radius: 2mm;
            padding: 0;
            box-sizing: border-box;
            background: white;
            display: flex;
            flex-direction: column;
            position: relative;
            page-break-inside: avoid;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .no-print {
            display: none !important;
          }
        }
        
        @media screen {
          .print-container {
            width: 297mm;
            height: 210mm;
            margin: 0 auto;
            padding: 3mm;
            background: #f5f5f5;
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            grid-template-rows: repeat(5, 1fr);
            gap: 2mm;
          }
          
          .voucher-card {
            width: 100%;
            height: 100%;
            aspect-ratio: 1 / 1;
            border: 2px solid #e5e7eb;
            border-radius: 2mm;
            padding: 0;
            box-sizing: border-box;
            background: white;
            display: flex;
            flex-direction: column;
            position: relative;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
        }
        
        .voucher-bg {
          position: absolute;
          top: -30%;
          right: -30%;
          width: 140%;
          height: 160%;
          background: linear-gradient(135deg, transparent 48%, #f3f4f6 48%, #f3f4f6 52%, transparent 52%);
          z-index: 0;
          pointer-events: none;
          opacity: 0.6;
        }
        
        .voucher-content {
          position: relative;
          z-index: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 2mm 2mm 0.5mm 2mm;
        }
        
        .voucher-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 0.8mm;
          padding-bottom: 0.8mm;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
        }
        
        .voucher-number {
          position: absolute;
          top: 0;
          left: 0;
          font-size: 7pt;
          font-weight: 800;
          color: #6b7280;
          padding: 0.8mm 1.5mm;
          background: #f3f4f6;
          border-radius: 0 0 1.5mm 0;
        }
        
        .logo-section {
          margin-bottom: 0.8mm;
        }
        
        .logo-icon {
          width: 23mm;
          height: 8mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .logo-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .price-tag {
          font-size: 10pt;
          font-weight: 800;
          line-height: 1;
          text-align: center;
          padding: 0.8mm 2.5mm;
          border-radius: 1mm;
          display: inline-block;
        }
        
        .voucher-body {
          flex: 1;
          display: flex;
          gap: 2mm;
          align-items: center;
          margin-bottom: 0.5mm;
        }
        
        .code-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.8mm;
        }
        
        .code-label {
          font-size: 4.5pt;
          color: #6b7280;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.15mm;
        }
        
        .code-value {
          font-size: 8pt;
          font-weight: 800;
          letter-spacing: 0.3mm;
          padding: 0.8mm 0;
          border-radius: 1mm;
          text-align: center;
        }
        
        .info-section {
          display: flex;
          flex-direction: column;
          gap: 0.5mm;
          margin-top: 1mm;
        }
        
        .info-item {
          font-size: 5.5pt;
          color: #374151;
          line-height: 1.4;
          font-weight: 600;
        }
        
        .info-item strong {
          font-weight: 800;
          color: #1f2937;
        }
        
        .qr-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        .qr-container {
          padding: 1mm;
          background: white;
          border-radius: 1mm;
          border-width: 2px;
          border-style: solid;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .voucher-footer {
          color: white;
          text-align: center;
          padding: 1.2mm 0.5mm;
          font-size: 5pt;
          font-weight: 900;
          letter-spacing: 0.15mm;
          margin: auto -2mm 0 -2mm;
          border-radius: 0 0 1.5mm 1.5mm;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 3.5mm;
        }
        
        .warning-text {
          margin: 0;
          line-height: 1.1;
        }
      `}</style>

      {vouchers.map((voucher, index) => {
        const color = index % 2 === 0 ? '#3b82f6' : '#f97316';
        return (
          <div key={voucher.id || index} className="voucher-card">
            <div className="voucher-bg"></div>
            <div className="voucher-content">
              {/* Header Section */}
              <div className="voucher-header">
                <div className="voucher-number">#{index + 1}</div>
                <div className="logo-section">
                  <div className="logo-icon">
                    <img src={companyLogo || ""} alt="Logo" />
                  </div>
                </div>
                <div className="price-tag" style={{ color, backgroundColor: `${color}15`, border: `1px solid ${color}` }}>
                  Rp.{(voucher.price || 0).toLocaleString('id-ID')}
                </div>
              </div>

              {/* Body Section */}
              <div className="voucher-body">
                <div className="code-section">
                  <div className="code-label">KODE VOUCHER</div>
                  <div className="code-value" style={{ color, backgroundColor: `${color}10` }}>
                    {voucher.voucher_code}
                  </div>
                  
                  {/* Info */}
                  <div className="info-section">
                    <div className="info-item">
                      <strong>Masa Aktif:</strong> {voucher.validity_days || 1} {voucher.validity_days === 1 ? 'Hari' : 'Jam'}
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="qr-section">
                  <div className="qr-container" style={{ borderColor: color }}>
                    <QRCodeSVG 
                      value={`username:${voucher.username};password:${voucher.password}`}
                      size={32}
                      level="M"
                    />
                  </div>
                </div>
              </div>

              {/* Footer Warning */}
              <div className="voucher-footer" style={{ background: color }}>
                <p className="warning-text">JANGAN DIBUANG SELAMA MASIH AKTIF    [!]</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(VoucherPrintTemplate);
